import { useEffect, useState } from "react"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"
import { useUiEditorState } from "@/tiptap/hooks/use-ui-editor-state"
import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"
import { useFloatingToolbarVisibility } from "@/tiptap/hooks/use-floating-toolbar-visibility"

// --- Node ---
import { ImageNodeFloating } from "@/tiptap/nodes/image-node/image-node-floating"

// --- Icons ---
import { MoreVerticalIcon } from "@/tiptap/icons/more-vertical-icon"

// --- UI ---
import { ColorTextPopover } from "@/tiptap/ui/color-text-popover"
import { FontSizePopover } from "@/tiptap/ui/font-size-popover"
// [AI DISABLED] import { ImproveDropdown } from "@/tiptap/ui/improve-dropdown"
import { LinkPopover } from "@/tiptap/ui/link-popover"
import { canToggleMark, MarkButton } from "@/tiptap/ui/mark-button"
import {
  canSetTextAlign,
  TextAlignButton,
} from "@/tiptap/ui/text-align-button"
import {
  canSetTextDirection,
  TextDirectionButton,
} from "@/tiptap/ui/text-direction-button"
import { TurnIntoDropdown } from "@/tiptap/ui/turn-into-dropdown"
import { IndentButton } from "@/tiptap/ui/indent-button"
import {
  canResetFormatting,
  ResetAllFormattingButton,
} from "@/tiptap/ui/reset-all-formatting-button"

// --- Utils ---
import { isSelectionValid } from "@/tiptap/lib/tiptap-collab-utils"

import { Button } from "@/tiptap/ui-primitive/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/tiptap/ui-primitive/popover"
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from "@/tiptap/ui-primitive/toolbar"

// --- UI Utils ---
import { FloatingElement } from "@/tiptap/ui-utils/floating-element"

const MOBILE_TOOLBAR_BREAKPOINT = 768

export function NotionToolbarFloating() {
  const { editor } = useTiptapEditor()
  const isMobile = useIsBreakpoint("max", MOBILE_TOOLBAR_BREAKPOINT)
  const { lockDragHandle, commentInputVisible } =
    useUiEditorState(editor)
  // [AI DISABLED] Add aiGenerationActive back to extraHideWhen when enabling AI:

  const { shouldShow } = useFloatingToolbarVisibility({
    editor,
    isSelectionValid,
    extraHideWhen: Boolean(commentInputVisible),
  })

  if (lockDragHandle || isMobile) return null

  return (
    <FloatingElement shouldShow={shouldShow}>
      <Toolbar variant="floating">
        {/* [AI DISABLED] <ToolbarGroup><ImproveDropdown hideWhenUnavailable={true} /></ToolbarGroup><ToolbarSeparator /> */}

        <ToolbarGroup>
          <TurnIntoDropdown hideWhenUnavailable={true} />
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <MarkButton type="bold" hideWhenUnavailable={true} />
          <MarkButton type="italic" hideWhenUnavailable={true} />
          <MarkButton type="underline" hideWhenUnavailable={true} />
          <MarkButton type="strike" hideWhenUnavailable={true} />
          <MarkButton type="code" hideWhenUnavailable={true} />
        </ToolbarGroup>

        <ToolbarSeparator />

        <ToolbarGroup>
          <ImageNodeFloating />
        </ToolbarGroup>

        <ToolbarGroup>
          <LinkPopover autoOpenOnLinkActive={false} hideWhenUnavailable={true} />
          <ColorTextPopover hideWhenUnavailable={true} />
          <FontSizePopover hideWhenUnavailable={true} />
        </ToolbarGroup>

        <MoreOptions hideWhenUnavailable={true} />
      </Toolbar>
    </FloatingElement>
  );
}

function canMoreOptions(editor) {
  if (!editor) {
    return false
  }

  const canTextAlignAny = ["left", "center", "right", "justify"].some((align) =>
    canSetTextAlign(editor, align))

  const canTextDirectionAny = ["ltr", "rtl"].some((direction) =>
    canSetTextDirection(editor, direction))

  const canMarkAny = ["superscript", "subscript"].some((type) =>
    canToggleMark(editor, type))

  return canMarkAny || canTextAlignAny || canTextDirectionAny || canResetFormatting(editor)
}

function shouldShowMoreOptions(params) {
  const { editor, hideWhenUnavailable } = params

  if (!editor) {
    return false
  }

  if (hideWhenUnavailable && !editor.isActive("code")) {
    return canMoreOptions(editor);
  }

  return Boolean(editor?.isEditable);
}

export function MoreOptions({
  editor: providedEditor,
  hideWhenUnavailable = false,
  ...props
}) {
  const { editor } = useTiptapEditor(providedEditor)
  const [show, setShow] = useState(false)

  useEffect(() => {
    if (!editor) return

    const handleSelectionUpdate = () => {
      setShow(shouldShowMoreOptions({
        editor,
        hideWhenUnavailable,
      }))
    }

    handleSelectionUpdate()

    editor.on("selectionUpdate", handleSelectionUpdate)

    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate)
    };
  }, [editor, hideWhenUnavailable])

  if (!show || !editor || !editor.isEditable) {
    return null
  }

  return (
    <>
      <ToolbarSeparator />
      <ToolbarGroup>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              role="button"
              tabIndex={-1}
              tooltip="More options"
              {...props}>
              <MoreVerticalIcon className="tiptap-button-icon" />
            </Button>
          </PopoverTrigger>

          <PopoverContent side="top" align="end" alignOffset={4} sideOffset={4} asChild>
            <Toolbar variant="floating" tabIndex={0}>
              <ToolbarGroup>
                <MarkButton type="superscript" />
                <MarkButton type="subscript" />
              </ToolbarGroup>

              <ToolbarSeparator />

              <ToolbarGroup>
                <TextAlignButton align="left" />
                <TextAlignButton align="center" />
                <TextAlignButton align="right" />
                <TextAlignButton align="justify" />
                <TextDirectionButton direction="ltr" />
                <TextDirectionButton direction="rtl" />
              </ToolbarGroup>

              <ToolbarSeparator />

              <ToolbarGroup>
                <IndentButton action="outdent" />
                <IndentButton action="indent" />
              </ToolbarGroup>

              <ToolbarSeparator />

              <ToolbarGroup>
                <ResetAllFormattingButton />
              </ToolbarGroup>
            </Toolbar>
          </PopoverContent>
        </Popover>
      </ToolbarGroup>
    </>
  );
}
